import api from './api';

interface PresignedUrlResponse {
  url: string;
  imageId: string;
  expiresIn: number;
}

/**
 * Get a presigned URL for an image
 * @param workspaceId - Workspace ID
 * @param imageId - Image ID
 * @param expiresIn - Expiration time in seconds (default: 3600 = 1 hour)
 * @returns Promise with the presigned URL
 */
export const getImageUrl = async (
  workspaceId: string,
  imageId: string,
  expiresIn: number = 3600
): Promise<string> => {
  try {
    const response = await api.get<PresignedUrlResponse>(
      `/workspaces/${workspaceId}/images/${imageId}/url`,
      {
        params: {
          expiresIn
        }
      }
    );

    return response.data.url;
  } catch (error) {
    console.error('Error fetching image URL:', error);
    throw error;
  }
};

/**
 * Cache for presigned URLs to avoid unnecessary API calls
 * Key format: `${workspaceId}:${imageId}`
 */
interface CachedUrl {
  url: string;
  expiresAt: number;
}

const urlCache = new Map<string, CachedUrl>();

/**
 * Get a presigned URL with caching
 * URLs are cached until 5 minutes before expiration to ensure they're always valid
 * @param workspaceId - Workspace ID
 * @param imageId - Image ID
 * @param expiresIn - Expiration time in seconds (default: 3600 = 1 hour)
 * @returns Promise with the presigned URL
 */
export const getImageUrlCached = async (
  workspaceId: string,
  imageId: string,
  expiresIn: number = 3600
): Promise<string> => {
  const cacheKey = `${workspaceId}:${imageId}`;
  const now = Date.now();

  // Check cache
  const cached = urlCache.get(cacheKey);
  if (cached && cached.expiresAt > now) {
    return cached.url;
  }

  // Fetch new URL
  const url = await getImageUrl(workspaceId, imageId, expiresIn);

  // Cache it (expire 5 minutes before actual expiration for safety)
  const safetyMargin = 5 * 60 * 1000; // 5 minutes in milliseconds
  urlCache.set(cacheKey, {
    url,
    expiresAt: now + (expiresIn * 1000) - safetyMargin
  });

  return url;
};

/**
 * Clear the URL cache for a specific image or all images
 * @param workspaceId - Workspace ID (optional)
 * @param imageId - Image ID (optional)
 */
export const clearImageUrlCache = (workspaceId?: string, imageId?: string) => {
  if (workspaceId && imageId) {
    urlCache.delete(`${workspaceId}:${imageId}`);
  } else {
    urlCache.clear();
  }
};
